import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {debounceTime, skip} from 'rxjs/operators';
import {MobileCheck} from '../../services/mobile-check/mobile-check.service';
import {NotificationService} from '../../services/notification/notification.service';
import {AlertUserConfig} from '../../models/alert-user-config';
import {AuthenticationService, DecodedJwt} from '@colimo/colimo-ui-library';
import {AlertUserDataPage} from '../../models/alert-user-data-page';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {AlertUserData} from '../../models/alert-user-data';
import {AlertUserStatus} from '../../models/alert-user-status';
import {KeyTextService} from '../../services/key-text/key-text.service';
import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';
import {PageSortParam} from '../../models/PageSortParam';
import {PageHeaderComponent} from "../../shared/components/page-header/page-header.component";
import {DatePipe, NgClass} from "@angular/common";
import {MatPaginator} from "@angular/material/paginator";
import {MatIcon} from "@angular/material/icon";
import {MatTableModule} from "@angular/material/table";
import {MatTab, MatTabContent, MatTabGroup, MatTabLabel} from "@angular/material/tabs";
import {MatButton} from "@angular/material/button";
import {MatCard} from "@angular/material/card";
import {MatSort} from "@angular/material/sort";
import {MatTooltip} from "@angular/material/tooltip";
import {AlertFilterDto} from "../../models/alert-filter-dto";
import {AlertPageRequestDto} from "../../models/alert-page-request-dto";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";

@Component({
  selector: 'colimo-notification-page',
  templateUrl: './notification-page.component.html',
  imports: [
    PageHeaderComponent,
    NgClass,
    MatIcon,
    TranslateModule,
    MatPaginator,
    MatCheckbox,
    MatTableModule,
    MatTab,
    MatTabGroup,
    MatTabLabel,
    MatTabContent,
    MatButton,
    MatCard,
    MatSort,
    DatePipe,
    MatTooltip,
    MatProgressBarModule,
    MatProgressSpinnerModule
  ],
  styleUrls: ['./notification-page.component.scss']
})
export class NotificationPageComponent implements OnInit, OnDestroy {

  public isMobile: boolean;
  public alertConfig: AlertUserConfig;
  public alertsConfigured: boolean;
  public alertUserData: AlertUserDataPage;
  public displayedColumns: string[] = ['alertLevel', 'colorName', 'productNr', 'batchNr', 'line', 'model', 'info', 'createdAt', 'linkToPlot', 'hideAlert'];
  public filterColumns: string[] = ['alertLevelFilter', 'colorNameFilter', 'productNrFilter', 'batchNrFilter', 'lineFilter', 'modelFilter', 'infoFilter'];
  public pageSizeOptions: number[] = [10, 20, 50, 100];
  public pageSize = 50;
  public currentPage = 0;
  public totalSize = 0;
  public selectedTab: number;
  public kpiWarnings = [ {name: 'CPI', value: 'kpiCpi'}, { name: 'TPI', value: 'kpiBpi'}, {name: 'LPI', value: 'kpiLpi'}, { name:'API', value: 'kpiApi'}, { name: 'CHI', value: 'kpiChi' }];
  public NBD_PREV_BATCH_ID: string = '#prevId';
  public loading: boolean = false;

  private stop$: Subject<boolean> = new Subject();
  private userId: string;
  private componentTranslations: string[] = [];
  private sortParams: PageSortParam[] = [];

  private filterChangedSubject = new Subject<void>();
  public filters: AlertFilterDto = {
    colorName: null,
    productNr: null,
    batchNr: null,
    line: null,
    model: null,
    info: null
  };

  constructor(private mobileCheck: MobileCheck,
              private notificationService: NotificationService,
              private authenticationService: AuthenticationService,
              private snackBar: MatSnackBar,
              private translateService: TranslateService,
              private keyTextService: KeyTextService,
              private router: Router) {
    const currentNavigation = this.router.getCurrentNavigation();
    if (currentNavigation && currentNavigation.extras && currentNavigation.extras.state && currentNavigation.extras.state.selectedTab) {
      this.selectedTab = currentNavigation.extras.state.selectedTab;
    } else {
      this.selectedTab = 0;
    }
  }

  ngOnInit(): void {
    this.keyTextService.getComponentTranslationsObservable().pipe(takeUntil(this.stop$)).subscribe({
      next: (compTranslations: string[]) => {
        this.componentTranslations = compTranslations;
        //this.loadAlerts();
      }, error: () => {
        // nothing
      }
    });

    // #441303 skip the first value because it might be outdated so we refresh the config from server at the end of this method and use the refreshed value
    this.notificationService.getAlertUserConfigObservable().pipe(skip(1)).pipe(takeUntil(this.stop$)).subscribe({
      next: (alertConfig: AlertUserConfig) => {
        this.alertConfig = alertConfig;
        this.alertsConfigured = this.notificationService.alertsConfigured(this.alertConfig);
        this.loadAlerts();
        this.showInfoMsg(this.translateService.instant('notification.configSavedSuccess'));
      }, error: () => {
        // nothing
        this.showInfoMsg(this.translateService.instant('notification.restError'));
      }
    });

    this.mobileCheck.getScreenSize().pipe(takeUntil(this.stop$)).subscribe((device) => {
      this.isMobile = device.isMobile;
      if (this.isMobile) {
        // remove link to plots from displayed columns if mobile display
        this.displayedColumns = ['alertLevel', 'colorName', 'productNr', 'batchNr', 'line', 'model', 'info', 'createdAt', 'hideAlert'];
      } else {
        this.displayedColumns = ['alertLevel', 'colorName', 'productNr', 'batchNr', 'line', 'model', 'info', 'createdAt', 'linkToPlot', 'hideAlert'];
      }
    });

    this.authenticationService.authentication.pipe(takeUntil(this.stop$)).subscribe({next: (decodedToken: DecodedJwt) => {
      this.userId = decodedToken.sub;
    }});

    // #441303 refresh alert config () which also refreshes alertCount // this.notificationService.updateAlertCount();
    this.notificationService.refreshAlertConfig();
    this.alertConfig = {} as AlertUserConfig;
    this.alertUserData = {} as any;
    this.alertUserData.content = [];

    this.filterChangedSubject.pipe(
      debounceTime(1000)
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadAlerts();
    });
  }

  public ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.unsubscribe();
  }

  public saveAlertUserConfig() {
    // this will update the alert config and triggers the observable next so a reload via the observable change
    this.notificationService.saveAlertConfig(this.alertConfig);
  }

  public routeToAlertDetails(row: AlertUserData): void {
    let routerLink = ['/project', row.colorId];
    // #433972 for direct link alert new batch deviation to new plot avg compare
    if (row.typ === 'NBD' && row.info.indexOf(this.NBD_PREV_BATCH_ID) > -1) {
      routerLink = routerLink.concat(['color', 'multi-batch']);
      // extract the previous batch id from the info of the alert
      const prevBatchId = row.info.substring(row.info.indexOf(this.NBD_PREV_BATCH_ID) + this.NBD_PREV_BATCH_ID.length, row.info.length);
      routerLink = routerLink.concat([prevBatchId + ',' + row.batchId]); // comma seperated batchIds from previous and deviated batch
      routerLink = routerLink.concat(['line', row.line + ',' + row.line, 'chart', 'mwgcomp']); // comma separated line which is the same for previous and deviated batch
    } else {
      routerLink = routerLink.concat(['batch', row.batchId, 'line', row.line]);
      if (row.model) {
        routerLink = routerLink.concat(['model', row.model]);
      }
      routerLink = routerLink.concat(['chart', this.getChartForAlertType(row)]);
    }
    routerLink = routerLink.concat(['0']);

    // open chart in new tab with the preselected tab corresponding to the alert type
    const selectedTab = this.getSelectedTabForAlertType(row);
    let queryParams: { queryParams: any } = { queryParams: { selectedTab } };

    // #574600 - Extended SPC should also preselect angle and component group
    if (row.typ === 'SPC') {
      // extract angle, start date and component group from info of the Extended SPC alert
      //  e.g. "info" : "SPC accumulated 61 of 428 <dA> measurements below the control limit in left components at a 45° - since 2023-06-21 08:33:35",
      const angleMatch = row.info.match(/(\d+)°/);
      const groupMatch = row.info.match(/\b(left|right|other)\s+components\b/i);
      // const dateMatch = row.info.match(/since\s+(\d{4}-\d{2}-\d{2})/);

      const angle = angleMatch ? Number(angleMatch[1]) : null;
      const componentGroup = groupMatch?.[1]?.toUpperCase() as 'LEFT' | 'RIGHT' | 'OTHER' | undefined;
      // const sinceDate = dateMatch ? dateMatch[1] : null; // will perhaps also pre preselected in the future

      if (angle != null) {
        queryParams.queryParams.selectedAngle = angle;
      }

      if (componentGroup) {
        queryParams.queryParams.selectedComponentGroup = componentGroup;
      }
    }

    const url = this.router.serializeUrl(this.router.createUrlTree(routerLink, queryParams));
    window.open('/#' + url, '_blank');
  }

  private getChartForAlertType(data: AlertUserData): string {
    switch (data.typ) {
      case 'WP':
        return 'lpg';
      case 'CP':
        return 'mwg';
      case 'SPC':
        return 'fdg';
      default:
        return 'mwg';
    }
  }

  private getSelectedTabForAlertType(data: AlertUserData): number {
    switch (data.typ) {
      case 'WP':
      case 'NBD':
        return 3;
      case 'CP':
        return 0;
      case 'SPC':
        return 1;
      default:
        return 0;
    }
  }

  public markAsRead(row: AlertUserData): void {
    // status 1 is read
    if (row.readStatus === 0) {
      const alertUserStatus: AlertUserStatus = {userId: this.userId, status: 1, alertId: row.alertId};
      this.notificationService.updateAlertStatus(alertUserStatus).subscribe({
        next: (status: AlertUserStatus) => {
          row.readStatus = status.status;
          this.notificationService.updateAlertCount();
        },
        error: () => {
          // nothing
          this.showInfoMsg(this.translateService.instant('notification.restError'));
        }
      });
    }
  }

  public markAllAsRead(): void {
    // get the first id because that is the newest alertId
    if (this.alertUserData && this.alertUserData.content && this.alertUserData.content.length > 0) {
      // we want to mark all warnings all read so get the max alertId
      const alertId = Math.max(...this.alertUserData.content.map(alert => alert.alertId));
      this.notificationService.updateAllAlertStatusUntil(this.userId, alertId, 1).subscribe({
        next: (updated: number) => {
          this.loadAlerts();
          this.notificationService.updateAlertCount();
          this.showInfoMsg(updated + this.translateService.instant('notification.markedAsRead'));
        }, error: () => {
          // nothing
          this.showInfoMsg(this.translateService.instant('notification.restError'));
        }
      });
    }
  }

  public markAsHidden(alertId: number): void {
    const alertUserStatus: AlertUserStatus = { userId: this.userId, status: 2, alertId: alertId}
    this.notificationService.updateAlertStatus(alertUserStatus).subscribe({
      next: (status: AlertUserStatus) => {
        // refresh alerts the hidden one should be gone
        this.loadAlerts();
        this.notificationService.updateAlertCount();
      },
      error: () => {
        // nothing
        this.showInfoMsg(this.translateService.instant('notification.restError'));
      }
    });
  }

  public pageEvent(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadAlerts();
  }

  public sortEvent(event: any) {
    if (event.direction === '') {
      // remove if present
      this.sortParams.splice(this.sortParams.findIndex(param => param.active === event.active));
    } else {
      const index = this.sortParams.findIndex(param => param.active === event.active)
      // already present then update direction
      if (index >= 0) {
        this.sortParams[index].direction = event.direction;
      }
      // add if not present
      else {
        this.sortParams.push({ active: event.active, direction: event.direction });
      }
    }
    this.loadAlerts();
  }

  public alertCheckboxChanged(event: MatCheckboxChange, name: string): void {
    this.alertConfig[name] = event.checked ? 2 : 0;
  }

  public alertKpiAllCheckboxChanged(event: MatCheckboxChange): void {
    if (event.checked) {
      this.alertConfig.kpiCpi = 2;
      this.alertConfig.kpiBpi = 2;
      this.alertConfig.kpiLpi = 2;
      this.alertConfig.kpiApi = 2;
      this.alertConfig.kpiChi = 2;
    } else {
      this.alertConfig.kpiCpi = 0;
      this.alertConfig.kpiBpi = 0;
      this.alertConfig.kpiLpi = 0;
      this.alertConfig.kpiApi = 0;
      this.alertConfig.kpiChi = 0;
    }
  }

  public filterChanged(field: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.filters[field] = value || null;
    if (value.length >= 3 || value.length === 0) {
      this.filterChangedSubject.next();
    }
  }

  public clearFilter(field: keyof AlertFilterDto): void {
    this.filters[field] = null;
    this.currentPage = 0;
    this.loadAlerts();
  }

  private loadAlerts(): void {
    this.loading = true;

    const request: AlertPageRequestDto = {
      page: this.currentPage,
      size: this.pageSize,
      sortColumn: 'createdAt',//this.sort?.active,
      sortDirection: 'DESC',//this.sort?.direction,
      filters: this.filters
    };
    this.notificationService.findAlerts(request).subscribe({
      next: (response: AlertUserDataPage) => {
        this.alertUserData = this.translatePossibleInfos(response);
        this.totalSize = response.page.totalElements;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showInfoMsg(this.translateService.instant('notification.restError'));
      }
    });
  }

  private translatePossibleInfos(data: AlertUserDataPage): AlertUserDataPage {
    if (data && data.content && data.content.length > 0) {
      for (let i = 0; i < data.content.length; i++) {
        // worst part components need translation
        if (data.content[i].typ === 'WP') {
          const translatedComponent = this.componentTranslations[data.content[i].component] ? this.componentTranslations[data.content[i].component] : data.content[i].component;
          data.content[i].info = data.content[i].info.replace('{component}', translatedComponent);
        }
      }
    }
    return data;
  }

  private showInfoMsg(msg: string): void {
    this.snackBar.open(msg, 'Close', { duration: 1500 });
  }

  protected readonly event = event;
}
